import { SKIP_AUTH_HEADER, request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

export const REGULATOR_LIST_API_PATH = '/api/v4/bit/web/regulator/disclosures/list'

export type RegulatorListItem = {
  [key: string]: unknown
  regulatorId: string
  regulatorCode: string
  regulatorShowName: string
  regulatorDescription: string
  foundationDate: string
  countryCode: string
  countryName: string
  countryFlag: string
  isOffshore: string
  logo: string
  id?: string
  code?: string
  regId?: string
  regInfo?: {
    fullName?: string
  }
  tag?: Record<string, unknown>
  traders?: Array<Record<string, unknown>>
  content?: string
  title?: string
  createTime?: string
  regTime?: string
}

export type RegulatorListData = {
  items: RegulatorListItem[]
  areaItems: Array<{
    areaCodeType: string
    areaCode: string
    areaName: string
  }>
}

export type RegulatorListParams = {
  areaCodeType?: string
  areaCode?: string
  cate?: 'all' | 1 | 2 | 3
  regulatorCodes?: string
  pageindex?: number
  pagesize?: number
}

const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const extractRegulatorListSource = (response: unknown): unknown => {
  let source = response

  for (let index = 0; index < 4 && source && typeof source === 'object'; index += 1) {
    if (Array.isArray(source)) return source

    const record = source as Record<string, unknown>
    const list = record.items ?? record.list ?? record.rows ?? record.records
    if (Array.isArray(list)) return { items: list, areaItems: record.areaItems }

    const next = record.data ?? record.Data ?? record.result
    if (!next || next === source) break
    source = next
  }

  return source
}

const normalizeRegulatorList = (response: unknown): RegulatorListData => {
  const source = extractRegulatorListSource(response)
  const sourceRecord =
    source && typeof source === 'object' && !Array.isArray(source) ? (source as Record<string, unknown>) : {}
  const items = Array.isArray(source) ? source : Array.isArray(sourceRecord.items) ? sourceRecord.items : []
  const areaItems = Array.isArray(sourceRecord.areaItems) ? sourceRecord.areaItems : []

  return {
    items: items
      .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
      .map((item) => ({
        ...item,
        regulatorId: normalizeText(item.regulatorId ?? item.RegulatorId),
        regulatorCode: normalizeText(item.regulatorCode ?? item.RegulatorCode),
        regulatorShowName: normalizeText(item.regulatorShowName ?? item.RegulatorShowName),
        regulatorDescription: normalizeText(item.regulatorDescription ?? item.RegulatorDescription),
        foundationDate: normalizeText(item.foundationDate ?? item.FoundationDate),
        countryCode: normalizeText(item.countryCode ?? item.CountryCode),
        countryName: normalizeText(item.countryName ?? item.CountryName),
        countryFlag: normalizeText(item.countryFlag ?? item.CountryFlag),
        isOffshore: normalizeText(item.isOffshore ?? item.IsOffshore),
        logo: normalizeText(item.logo ?? item.Logo),
        title: normalizeText(item.title ?? item.Title),
        regTime: normalizeText(item.regTime ?? item.RegTime),
      })),
    areaItems: areaItems
      .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
      .map((item) => ({
        areaCodeType: normalizeText(item.areaCodeType ?? item.AreaCodeType),
        areaCode: normalizeText(item.areaCode ?? item.AreaCode),
        areaName: normalizeText(item.areaName ?? item.AreaName),
      })),
  }
}

/** 获取监管机构列表，SSR 和客户端使用同一接口封装。 */
export const getRegulatorList = async (params: RegulatorListParams = {}): Promise<RegulatorListData> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: REGULATOR_LIST_API_PATH,
    method: 'GET' as const,
    params: {
      ...params,
    },
    headers: {
      [SKIP_AUTH_HEADER]: '1',
    },
  }

  const response = import.meta.server
    ? await useServerFetch<unknown>(requestConfig)
    : await request<unknown, unknown>(requestConfig)

  return normalizeRegulatorList(response)
}
